import {
  CheckOutlined,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { popupApi } from "api/popup.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { debounce } from "lodash";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import React, { useCallback, useEffect, useState } from "react";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { Popup } from "types/popup";
import { getTitle } from "utils";
import { $url } from "utils/url";
import { PopupModal } from "./components/PopupModal";
import { formatDate } from "utils/date";
import { areaStore } from "store/areaStore";

const { ColumnGroup, Column } = Table;
interface PopupQuery extends QueryParam {
  areaId: number | null;
}

export const HomePopupPage = ({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<PopupQuery>({
    page: 1,
    limit: 10,
    search: "",
    areaId: null,
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Popup[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState<Partial<Popup>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
    areaStore.fetchList();
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await popupApi.findAll(query);
    setLoading(false);
    setData(res.data.popups);
    setTotal(res.data.total);
  };

  const handleDelete = async (popup: Popup) => {
    setLoading(true);
    try {
      await popupApi.delete(popup.id);
      fetchData();
      message.info("Xóa thành công");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (popup: Popup) => {
    setLoading(true);
    try {
      if (popup.isVisible) {
        await popupApi.hide(popup.id);
        message.info("Ẩn thành công!");
      } else {
        await popupApi.show(popup.id);
        message.info("Hiển thị thành công!");
      }
    } finally {
      fetchData();
      setLoading(false);
    }
  };

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm kiếm",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                        }}
                      />
                    ),
                  },
                },
              }}
              leftActions={
                <AppButton
                  onClick={() => {
                    setVisibleModal(true);
                    setModalStatus("create");
                    setSelectedPopup({});
                  }}
                  tone="primary"
                  icon={<PlusOutlined />}
                >
                  Thêm mới
                </AppButton>
              }
              filterLabel={null}
            />
          }
          table={
            <AppTable<Popup>
              loading={loading}
              pagination={false}
              rowKey="id"
              dataSource={data}
            >
          <Column
            title="Tiêu đề"
            dataIndex="title"
            key="title"
            render={(text, record: Popup) => {
              return (
                <div>
                  <img
                    src={$url(record.image)}
                    width={50}
                    style={{ objectFit: "contain", borderRadius: 5 }}
                    alt=""
                  />
                  <br />
                  <label htmlFor="">{text}</label>
                </div>
              );
            }}
          />

          <Column title="Khu vực" dataIndex={["area", "name"]} key="areaName" />

          <Column
            title="Trạng thái"
            dataIndex="isVisible"
            key="isVisible"
            render={(text) => (
              <Tag color={text ? "green" : "red"}>
                {text ? "Hiển thị" : "Bị ẩn"}
              </Tag>
            )}
          />

          <Column
            title="Ngày tạo"
            dataIndex="createdAt"
            key="phone"
            render={(text) => <span>{formatDate(text)}</span>}
          />

          <Column
            title="Thao tác"
            key="action"
            width={130}
            align="center"
            render={(text, record: Popup) => (
              <div className="flex gap-2 justify-center items-center">
                <Tooltip title="Cập nhật">
                  <ActionButton
                    aria-label="Cập nhật"
                    onClick={() => {
                      setSelectedPopup(record);
                      setVisibleModal(true);
                      setModalStatus("update");
                    }}
                  >
                    <EditOutlined style={{ color: "#6c84e5" }} />
                  </ActionButton>
                </Tooltip>
                <Tooltip title={record.isVisible ? "Ẩn" : "Hiển thị"}>
                  <Popconfirm
                    placement="topLeft"
                    title={`Xác nhận ${record.isVisible ? "ẩn" : "hiển thị"} popup này?`}
                    onConfirm={() => handleToggle(record)}
                    okText="Ok"
                    cancelText="Không"
                  >
                    <ActionButton aria-label={record.isVisible ? "Ẩn" : "Hiển thị"}>
                      {record.isVisible ? (
                        <EyeInvisibleOutlined style={{ color: "#f59e0b" }} />
                      ) : (
                        <CheckOutlined style={{ color: "#22c55e" }} />
                      )}
                    </ActionButton>
                  </Popconfirm>
                </Tooltip>
                <Tooltip title="Xóa">
                  <Popconfirm
                    placement="top"
                    title={"Xác nhận xóa popup này?"}
                    onConfirm={() => handleDelete(record)}
                    okText="Xóa"
                    cancelText="Không"
                  >
                    <ActionButton aria-label="Xóa">
                      <DeleteOutlined style={{ color: "#ef4444" }} />
                    </ActionButton>
                  </Popconfirm>
                </Tooltip>
              </div>
            )}
          />
            </AppTable>
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                query.page = page;
                query.limit = limit;
                setQuery({ ...query });
              }}
            />
          }
        />

      <PopupModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        popup={selectedPopup}
        status={modalStatus}
      />
    </div>
  );
};
